import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/dialog";
import { FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
} from "@/components/ui/select";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { useActiveOrganization } from "@/hooks/organizations";
import { useWorkspaces } from "@/queries";
import {
  useCreateOrganization,
  useOrganizations,
} from "@/queries/organizations";
import { BaseOrganization } from "@/types/organizations";

import { Building2, PlusSquare } from "lucide-react";

import { useState } from "react";

import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import SelectItemIcon from "./SelectItemIcon";

const CreateOrganizationForm = ({ onClose }: { onClose: () => void }) => {
  const create = useCreateOrganization();
  const form = useForm<BaseOrganization>({
    defaultValues: {
      name: "",
    },
  });

  const handleSubmit = async (data: BaseOrganization) => {
    await create.mutateAsync(data);
    onClose();
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h3 className="text-lg font-semibold text-gray-900">
          Create Organization
        </h3>
        <p className="text-sm text-gray-600">
          Organizations help you manage teams and workspaces.
        </p>
      </div>

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel className="text-sm font-medium text-gray-700">
                  Organization name
                </FormLabel>
                <Input
                  placeholder="e.g. Acme Corp"
                  {...field}
                  className="h-10 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                />
              </FormItem>
            )}
          />
          <DialogFooter className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 "
              disabled={create.isLoading}
            >
              {create.isLoading ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </FormProvider>
    </div>
  );
};

const SelectOrganization = () => {
  const organizationsQuery = useOrganizations();
  const activeOrganization = useActiveOrganization();
  const workspacesQuery = useWorkspaces();
  const organizationIds =
    workspacesQuery.data?.workspaces.map(
      (workspace) => workspace.organization_id,
    ) || [];

  const organizations =
    organizationsQuery.data?.items.filter((organization) =>
      organizationIds.includes(organization.id),
    ) || [];

  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const defaultWorkspaceLink = (organization_id: number) => {
    const workspaces =
      workspacesQuery.data?.workspaces.filter(
        (workspace) => workspace.organization_id === organization_id,
      ) || [];
    const defaultWorkspace = workspaces.find(
      (workspace) => workspace.is_default,
    );

    if (defaultWorkspace) return `/workspace/${defaultWorkspace.id}/home`;
    if (workspaces.at(0)) return `/workspace/${workspaces.at(0)?.id}/home`;
    return `/home`;
  };

  const handleSelect = (value: string) => {
    if (value === "create-organization") {
      setOpen(true);
    } else {
      const organizationId = parseInt(value);
      navigate(defaultWorkspaceLink(organizationId));
    }
  };

  return (
    <>
      <Select
        value={activeOrganization?.id.toString()}
        onValueChange={handleSelect}
      >
        <SelectTrigger className="h-8 min-w-[200px] max-w-[200px] whitespace-nowrap border-gray-200  shadow-sm transition-colors duration-150 hover:border-gray-300 hover:bg-gray-50">
          <div className="flex w-full items-center gap-2 text-sm">
            {activeOrganization ? (
              <>
                <SelectItemIcon>
                  {activeOrganization.name.charAt(0).toUpperCase()}
                </SelectItemIcon>
                <OverflowDisplayTooltip
                  content={activeOrganization.name}
                  side="bottom"
                  className="max-w-[96px] truncate font-medium text-gray-900"
                >
                  {activeOrganization.name}
                </OverflowDisplayTooltip>
              </>
            ) : (
              <>
                <Building2 className="h-4 w-4 flex-shrink-0 text-gray-400" />
                <span className="text-gray-500">Select organization</span>
              </>
            )}
          </div>
        </SelectTrigger>
        <SelectContent
          align="start"
          className="w-[256px] border-gray-200 shadow-lg"
        >
          <SelectGroup className="max-h-[450px] w-full overflow-y-auto overflow-x-hidden">
            <SelectLabel className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
              Organizations
            </SelectLabel>
            {organizations.sort().map((organization) => (
              <SelectItem
                key={`organization-${organization.id}`}
                value={organization.id.toString()}
                className="w-full cursor-pointer px-3 py-2"
              >
                <div className="flex w-full items-center gap-3">
                  <SelectItemIcon>
                    {organization.name.charAt(0).toUpperCase()}
                  </SelectItemIcon>
                  <OverflowDisplayTooltip
                    content={organization.name}
                    side="top"
                    className="min-w-0 max-w-[156px] font-medium text-gray-900"
                  >
                    {organization.name}
                  </OverflowDisplayTooltip>
                </div>
              </SelectItem>
            ))}
            <SelectSeparator className="my-1 bg-gray-100" />
          </SelectGroup>
          <SelectItem
            value="create-organization"
            className="cursor-pointer px-3 py-2 hover:bg-gray-50 focus:bg-gray-50"
          >
            <div className="flex items-center gap-3">
              <PlusSquare className="h-5 w-5 text-gray-400" />
              <span className="font-medium text-gray-700">
                Create organization
              </span>
            </div>
          </SelectItem>
        </SelectContent>
      </Select>
      {open && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="border-gray-200 shadow-xl sm:max-w-md">
            <CreateOrganizationForm onClose={() => setOpen(false)} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default SelectOrganization;
